import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";
import type { evereeFile } from "types/api-types/payroll/everee-files/everee-files.types";

export type WorkerStatus = "ACTIVE" | "INACTIVE" | "PENDING" | "TERMINATED" | "";
export type WorkerType = "employee_invite" | "contractor_invite" | "employee_complete" | "";

/**
 * EvereeWorkerManagementStore - Manages worker data for admin interface
 */
class EvereeWorkerManagementStore {
  rootStore: RootStore;

  // Observable Properties
  isLoading = false;
  error: string | null = null;
  searchTerm = "";
  statusFilter: WorkerStatus = "";
  workerTypeFilter: WorkerType = "";
  selectedWorkerId: string | null = null;
  selectedWorkerData: evereeWorkerData | null = null;
  selectedWorkerFiles: evereeFile[] = [];

  // DataGrid state - uses DataGridColumnHeader format (key, title, width)
  currentHeaders: Array<{ key: string; title: string; width: number }> = [
    { key: "workerId", title: "Worker ID", width: 120 },
    { key: "firstName", title: "First Name", width: 120 },
    { key: "lastName", title: "Last Name", width: 120 },
    { key: "email", title: "Email", width: 200 },
    { key: "phoneNumber", title: "Phone", width: 140 },
    { key: "status", title: "Status", width: 100 },
    { key: "workerType", title: "Type", width: 120 },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessors
  get evereeWorkersStore(): ServiceStore {
    return this.rootStore.evereeWorkersStore as ServiceStore;
  }

  get evereeEmbeddedWorkersStore(): ServiceStore {
    return this.rootStore.evereeEmbeddedWorkersStore as ServiceStore;
  }

  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  get evereeCandidatesStore(): ServiceStore {
    return this.rootStore.evereeCandidatesStore as ServiceStore;
  }

  get evereeFilesStore(): ServiceStore {
    return this.rootStore.evereeFilesStore as ServiceStore;
  }

  // Computed Values
  get workers(): evereeWorkerData[] {
    return (this.evereeWorkerDataStore?.entities || []) as unknown as evereeWorkerData[];
  }

  get filteredWorkers(): evereeWorkerData[] {
    let result = this.workers;

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter((w) => {
        // API returns 'id' but type expects 'workerId' - handle both
        const workerId = (w as unknown as Record<string, unknown>).id as string || w.workerId;
        return (
          w.firstName?.toLowerCase().includes(term) ||
          w.lastName?.toLowerCase().includes(term) ||
          w.email?.toLowerCase().includes(term) ||
          workerId?.toLowerCase().includes(term)
        );
      });
    }

    if (this.statusFilter) {
      // Filter by onboarding status - ACTIVE means onboardingComplete=true
      const isActive = this.statusFilter === "ACTIVE";
      result = result.filter((w) => w.onboardingComplete === isActive);
    }

    // workerType filter not applicable for everee-worker-data API
    // Workers from this endpoint don't have workerType distinction

    return result;
  }

  get dataGridData(): Record<string, unknown>[] {
    return this.filteredWorkers.map((w) => {
      // API returns 'id' but type expects 'workerId' - handle both
      const workerId = (w as unknown as Record<string, unknown>).id as string || w.workerId;
      return {
        id: workerId,
        workerId: workerId,
        firstName: w.firstName,
        lastName: w.lastName,
        email: w.email,
        phoneNumber: w.phoneNumber,
        status: w.onboardingComplete ? "ACTIVE" : "PENDING",
        workerType: (w as unknown as Record<string, unknown>).workerType || "-",
      };
    });
  }

  get totalCount(): number {
    return this.workers.length;
  }

  get filteredCount(): number {
    return this.filteredWorkers.length;
  }

  // Actions
  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setStatusFilter = (status: WorkerStatus) => {
    this.statusFilter = status;
  };

  setWorkerTypeFilter = (type: WorkerType) => {
    this.workerTypeFilter = type;
  };

  setSelectedWorkerId = (workerId: string | null) => {
    this.selectedWorkerId = workerId;
    if (!workerId) {
      this.selectedWorkerData = null;
      this.selectedWorkerFiles = [];
    }
  };

  clearFilters = () => {
    this.searchTerm = "";
    this.statusFilter = "";
    this.workerTypeFilter = "";
  };

  // Async Actions
  loadWorkers = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;

    try {
      // Build query with company filter if provided
      const query: Record<string, unknown> = { page: 0, size: 100 };
      if (companyId) {
        query.companyId = companyId;
      }

      // Use everee-worker-data service for listing workers (everee-workers only supports create/remove)
      // Backend limits size to max 100 per request
      await this.evereeWorkerDataStore.find({ query });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load workers";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  loadWorkerDetails = async (workerId: string, companyId?: string) => {
    this.selectedWorkerId = workerId;
    this.selectedWorkerData = null;
    this.selectedWorkerFiles = [];

    try {
      // Load worker data with companyId
      const query: Record<string, unknown> = {};
      if (companyId) {
        query.companyId = companyId;
      }
      const workerData = await this.evereeWorkerDataStore.get(workerId, { query });

      // Load worker files
      const filesQuery: Record<string, unknown> = { workerId, $limit: 100 };
      if (companyId) {
        filesQuery.companyId = companyId;
      }
      const filesResult = await this.evereeFilesStore.find({
        query: filesQuery,
      });

      runInAction(() => {
        this.selectedWorkerData = workerData as unknown as evereeWorkerData;
        this.selectedWorkerFiles = (filesResult?.data || filesResult || []) as unknown as evereeFile[];
      });
    } catch (error) {
      console.error("Failed to load worker details:", error);
    }
  };

  createWorker = async (
    workerData: Record<string, unknown>
  ): Promise<evereeWorkerData | null> => {
    this.isLoading = true;
    this.error = null;

    try {
      // Use everee-workers service for creating workers (it supports create)
      const result = await this.evereeWorkersStore.create(workerData);
      await this.loadWorkers(); // Refresh list from everee-worker-data
      return result as unknown as evereeWorkerData;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to create worker";
      });
      return null;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateWorkerData = async (
    workerId: string,
    data: Partial<evereeWorkerData>
  ): Promise<boolean> => {
    try {
      await this.evereeWorkerDataStore.patch(workerId, data);
      await this.loadWorkerDetails(workerId);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update worker";
      });
      return false;
    }
  };

  uploadFile = async (workerId: string, fileData: FormData): Promise<boolean> => {
    try {
      await this.evereeFilesStore.create({
        workerId,
        ...Object.fromEntries(fileData),
      });
      await this.loadWorkerDetails(workerId);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to upload file";
      });
      return false;
    }
  };

  deleteFile = async (fileUrl: string): Promise<boolean> => {
    try {
      await this.evereeFilesStore.remove(fileUrl);
      if (this.selectedWorkerId) {
        await this.loadWorkerDetails(this.selectedWorkerId);
      }
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to delete file";
      });
      return false;
    }
  };

  // Reset store state
  reset = () => {
    this.isLoading = false;
    this.error = null;
    this.searchTerm = "";
    this.statusFilter = "";
    this.workerTypeFilter = "";
    this.selectedWorkerId = null;
    this.selectedWorkerData = null;
    this.selectedWorkerFiles = [];
  };
}

export default EvereeWorkerManagementStore;
